import { EmployeeCustomerSurveyTypeMapDto, GetUsersByAccountSurveyTypeResponse, QueueTypeEnum, SurveyTypeListDto } from "interfaces/Mueller.Business.Models";
import React, { useContext, useEffect, useRef, useState } from "react";
import { addMapping, getUsersByAccountSurveyType } from "requests/employeeCustomerSurveyTypes";
import { connect } from "react-redux";
import responseHandler from "responseHandler";
import { MGrid, MSpinner } from "components/Shared";
import EmployeeMappings from "./EmployeeMappings";
import { DATA_TYPE_BOOLEAN } from "typeConstants/dataTypes";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import AddAgentDialogContent from "./AddAgentDialogContent";
import { BUSOP_AGENT, QUALITY_COMPLIANCE_AUDITOR, BUSOP_MANAGER, CUSTOMER_EXPERIENCE_MANAGER, CUSTOMER_EXPERIENCE_REP } from "typeConstants/employeeRoles";
import { getSurveyTypesByCustomer } from "requests/customer";
import { MSelectOption } from "components/Shared/MSelect";
import { auditQAQueueTypeMSelectOption, fieldAppointmentQueueTypeMSelectOption, homeownersQaQueueTypeMSelectOption, preferredAppointmentQueueTypeMSelectOption, queueTypesToEnumMap } from "typeConstants/employeeCustomerSurveyTypes";
import { MGridAction } from "components/Shared/MGrid";

/* eslint-disable react/prop-types */ // Using TypeScript interface for props validation

interface QueueMappingDetailsProps {
  employeeCustomerSurveyTypeMap: EmployeeCustomerSurveyTypeMapDto,
  queueType: QueueTypeEnum,
  gridWrapperStyle?: React.CSSProperties,
  //Redux
  makeErrorToast: (test: string) => void,
}

const defaultGridWrapperStyle: React.CSSProperties = { maxHeight: 400 };

const QueueMappingDetails = (props: QueueMappingDetailsProps) => {

  const [isFetchingUsersByAccountSurveyType, setIsFetchingUsersByAccountSurveyType] = useState<boolean>(true);
  const [accountSurveyTypeUsers, setAccountSurveyTypeUsers] = useState<GetUsersByAccountSurveyTypeResponse[]>([]);
  const [selectedAgentEmployeeId, setSelectedAgentEmployeeId] = useState<number>();
  const [isAddingAgent, setIsAddingAgent] = useState<boolean>(false);
  const [isFetchingSurveyTypes, setIsFetchingSurveyTypes] = useState<boolean>(true);
  const [customerSurveyTypes, setCustomerSurveyTypes] = useState<SurveyTypeListDto[]>([]);

  const agentToAddEmployeeId = useRef<number>();
  const mounted = useRef<boolean>(true);
  const dialog = useContext<DialogProviderValue>(DialogContext);

  useEffect(() => {
    return () => { mounted.current = false; };
  },
    []);

  useEffect(() => {
    getUsers();
    //We need to fetch the customer's available survey types because the survey type code isn't a property of EmployeeCustomerSurveyTypeMapDto
    getCustomerSurveyTypes();
  },
    [props.employeeCustomerSurveyTypeMap, props.queueType]);

  const getUsers = () => {
    if (!isFetchingUsersByAccountSurveyType)
      setIsFetchingUsersByAccountSurveyType(true);

    getUsersByAccountSurveyType(props.queueType, props.employeeCustomerSurveyTypeMap.customerId, props.employeeCustomerSurveyTypeMap.surveyTypeId,
      (response) => {
        setAccountSurveyTypeUsers(response.sort((a, b) =>  a.firstLastNameOnly.localeCompare(b.firstLastNameOnly)));
        setIsFetchingUsersByAccountSurveyType(false);
      },
      () => {
        props.makeErrorToast("Failed to fetch users, please try again");
      },
      mounted
    );
  };

  const getCustomerSurveyTypes = () => {
    if (!isFetchingSurveyTypes)
      setIsFetchingSurveyTypes(true);
    getSurveyTypesByCustomer(
      props.employeeCustomerSurveyTypeMap.customerNumber,
      (response: SurveyTypeListDto[]) => {
        setCustomerSurveyTypes(response);
        setIsFetchingSurveyTypes(false);
      },
      mounted
    );
  };

  if (isFetchingUsersByAccountSurveyType || isAddingAgent)
    return <MSpinner />;

  const getEmployeeCustomerSurveyTypeDto = (employeeId: number, isDeleting?: boolean) => {
    return [
      {
        employeeId: !isDeleting ? employeeId : null,
        customerNumber: props.employeeCustomerSurveyTypeMap.customerNumber,
        surveyType: props.employeeCustomerSurveyTypeMap.surveyTypeId.toString(),
        queueTypeCode: queueTypesToEnumMap[props.queueType],
        customerName: !isDeleting ? props.employeeCustomerSurveyTypeMap.customerName : null,
        surveyTypeCode: customerSurveyTypes.find((cst) => cst.id === props.employeeCustomerSurveyTypeMap.surveyTypeId).code,
        isExcluded: false,
      }
    ];
  };

  const showOperationCompletedDialog = () => {
    dialog.showYesNo(
      "Refresh?",
      //Adding a mapping is done asynchronously on the back end, so getting the new users even after success may not yield expected results. User will have to refresh the pane to check new mappings
      "Queued mapping updates successfully! This may take some time. Would you like to refresh and check if mappings have been updated?",
      () => {
        getUsers();
        dialog.hide();
      },
      null,
      "Refresh",
      "I'll check later"
    );
  };

  const onAgentAddClick = () => {
    if (!agentToAddEmployeeId.current) {
      props.makeErrorToast("Please select an agent first");
      return false; //can't just use return here, have to use return false so the dialog stays open
    }
    setIsAddingAgent(true);
    const payload = getEmployeeCustomerSurveyTypeDto(selectedAgentEmployeeId);
    addMapping(
      agentToAddEmployeeId.current,
      payload,
      () => {
        showOperationCompletedDialog();
        setSelectedAgentEmployeeId(null);
        agentToAddEmployeeId.current = null;
        setIsAddingAgent(false);
      },
      () => {
        props.makeErrorToast("Failed to add employee mapping, please try again");
      },
      mounted
    );
  };

  //The card is titled "Employees Mapped" but that feels stupid to type over and over, so the functions have "MappedAgents" instead
  const onMappedAgentsAddClick = () => {
    const dialogActions = [
      {
        label: "Close",
        isPrimary: false,
        onClick: () => dialog.hide(),
      },
      {
        label: "Add",
        isPrimary: true,
        onClick: onAgentAddClick,
      }
    ];
    dialog.show("Add Employee Mapping", getAddAgentDialogContent(), dialogActions);
  };

  const getEmployeeRolesForSelectedQueueType = () => {
    if (props.queueType === QueueTypeEnum.HomeownersQa || props.queueType === QueueTypeEnum.AuditQa)
      return [BUSOP_AGENT, QUALITY_COMPLIANCE_AUDITOR, BUSOP_MANAGER];
    else
      return [CUSTOMER_EXPERIENCE_REP, CUSTOMER_EXPERIENCE_MANAGER];
  };

  const onAgentSelectedCallback = (selectedOption: MSelectOption) => {
    if(selectedOption)
      //@ts-ignore
      agentToAddEmployeeId.current = selectedOption.value;
    else
      agentToAddEmployeeId.current = null;
  };

  const getAddAgentDialogContent = () => {
    return (
      <AddAgentDialogContent
        //@ts-ignore
        onAgentSelectCallback={onAgentSelectedCallback}
        employeeRoles={getEmployeeRolesForSelectedQueueType()}
        excludedEmployeeIds={accountSurveyTypeUsers.map(u => u.employeeId)}
      />);
  };

  const getMappedAgentsGridColumnDefs = () => {
    return [
      {
        key: `agent-name-${props.employeeCustomerSurveyTypeMap.customerId}-${props.queueType}-${props.employeeCustomerSurveyTypeMap.surveyTypeId}`,
        name: "Employee",
        dataSource: "firstLastNameOnly"
      },
      {
        key: `active-${props.employeeCustomerSurveyTypeMap.customerId}-${props.queueType}-${props.employeeCustomerSurveyTypeMap.surveyTypeId}`,
        name: "Active",
        dataSource: "isClockedIn",
        dataType: DATA_TYPE_BOOLEAN,
      }
    ];
  };

  const onMappedAgentsRowClicked = (selectedRowIndex: number) => {
    setSelectedAgentEmployeeId(accountSurveyTypeUsers[selectedRowIndex].employeeId);
  };

  const getMappedAgentsGridActions = (): MGridAction[] => [{ icon: "add", onClick: onMappedAgentsAddClick, helpText: "Add Employees", }];

  const getMappedAgentsGrid = () => {
    return (
      <MGrid
        data={accountSurveyTypeUsers}
        columnDefinitions={getMappedAgentsGridColumnDefs()}
        onRowClick={(row) => onMappedAgentsRowClicked(row)}
        cardStyle
        headerText="Employees Mapped"
        actions={getMappedAgentsGridActions()}
        className="full-height md-cell md-cell--12"
        wrapperStyle={props.gridWrapperStyle || defaultGridWrapperStyle}
      />
    );
  };

  const getQueueTypesOverride = () => {
    if (props.queueType === QueueTypeEnum.HomeownersQa || props.queueType === QueueTypeEnum.AuditQa)
      return [homeownersQaQueueTypeMSelectOption, auditQAQueueTypeMSelectOption];

    return [fieldAppointmentQueueTypeMSelectOption, preferredAppointmentQueueTypeMSelectOption];
  };

  return (
    <div className="queue-mapping-details-container">
      {getMappedAgentsGrid()}
      {selectedAgentEmployeeId &&
      <div className="md-cell md-cell--12">
        <EmployeeMappings
          employeeId={selectedAgentEmployeeId}
          cardStyle={true}
          overrideQueueTypes={getQueueTypesOverride()}
          cardTitleOverride={`Mappings for ${accountSurveyTypeUsers.find(u => u.employeeId === selectedAgentEmployeeId)?.firstLastNameOnly}`}
        />
      </div>}
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  makeErrorToast(text: string) {
    responseHandler.customToast(text, "error", dispatch);
  },
});

export default connect(null, mapDispatchToProps)(QueueMappingDetails);
